// frontend/src/pages/BudgetPage.js
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// (임시 CSS)
const styles = {
  container: { padding: '20px' },
  filters: { 
    background: '#f8f9fa', padding: '15px', borderRadius: '5px', 
    display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '10px' // ⬅️ marginBottom 수정
  },
  yearInput: { padding: '8px', fontSize: '1rem' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
  },
  card: {
    background: 'white',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  },
  cardTitle: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#666',
    marginBottom: '10px',
  },
  cardValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '5px',
  },
  cardUnit: {
    fontSize: '1.2rem',
    color: '#333',
    marginLeft: '5px',
  },
  totalCard: { borderTop: '4px solid #007bff' },
  usedCard: { borderTop: '4px solid #ffc107' },
  paidCard: { borderTop: '4px solid #28a745' },
  remainingCard: { borderTop: '4px solid #dc3545' },
  
  tableContainer: {
    marginTop: '40px',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    minWidth: '800px',
    borderCollapse: 'collapse',
  },
  th: {
    border: '1px solid #ddd',
    padding: '12px',
    background: '#f4f4f4',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  td: {
    border: '1px solid #ddd',
    padding: '12px',
    textAlign: 'right', 
  },
  tdName: { 
    textAlign: 'left',
  },
  
  // ⬇️ [신규] 안내문 박스 스타일
  infoBox: {
    marginTop: '0px',
    marginBottom: '20px',
    background: '#fff9e6',
    border: '1px solid #ffc107',
    padding: '15px',
    borderRadius: '5px',
    fontSize: '0.9rem',
    lineHeight: '1.6'
  },
  infoList: {
    paddingLeft: '20px',
    margin: '10px 0 0 0'
  },
  excelButton: {
    backgroundColor: '#28a745',
    color: 'white',
    padding: '8px 14px',
    borderRadius: '5px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.95rem',
    marginLeft: 'auto'
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '15px'
  },
  monthlyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: '12px',
    marginTop: '15px'
  },
  monthlyCard: {
    padding: '14px',
    background: 'white',
    border: '1px solid #ddd',
    borderRadius: '8px',
    textAlign: 'center',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  }
};

const BudgetPage = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [baseMonth, setBaseMonth] = useState(new Date().getMonth() + 1);
  const [detailViewMode, setDetailViewMode] = useState('year'); // 'year' | 'monthly' | 'quarterly' | 'monthlyWithAdditional' | 'quarterlyWithAdditional'
  const [budgetData, setBudgetData] = useState({ summary: null, details: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchBudgetSummary = useCallback(async () => {
    if (!selectedYear) return;
    
    setLoading(true);
    setError('');
    setBudgetData({ summary: null, details: [] }); 
    
    try {
      const res = await api.get('/budget/summary', {
        params: { year: selectedYear, baseMonth }
      });
      setBudgetData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || '예산 요약 정보를 불러오는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, baseMonth]);

  // 2. Effect (변경 없음)
  useEffect(() => {
    fetchBudgetSummary();
  }, [fetchBudgetSummary]);

  const handleExcelExport = useCallback(async () => {
    if (!selectedYear) return;
    try {
      const res = await api.get('/budget/export', {
        params: { year: selectedYear, baseMonth },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `budget_${selectedYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || '예산 정보 엑셀 출력에 실패했습니다.');
    }
  }, [selectedYear, baseMonth]);

  const { summary, details } = budgetData;
  const formatCurrency = (num) => `${(num || 0).toLocaleString()} 원`;

  return (
    <div style={styles.container}>
      <h2>연차 예산 관리</h2>
      
      {/* 1. 필터 */}
      <div style={styles.filters}>
        <input
          type="number"
          style={styles.yearInput}
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          placeholder="연도 입력 (예: 2024)"
          min="2020"
          step="1"
        />
        <label>
          기준 인원
          <select
            style={{ marginLeft: '8px', padding: '6px 8px', fontSize: '0.9rem' }}
            value={baseMonth}
            onChange={(e) => setBaseMonth(Number(e.target.value))}
          >
            {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
              <option key={m} value={m}>{m}월</option>
            ))}
          </select>
        </label>
        <span>
          {selectedYear}년 전체 사업의 연차수당 예산을 요약합니다.
        </span>
        <button
          type="button"
          style={styles.excelButton}
          onClick={handleExcelExport}
          disabled={loading}
        >
          예산 정보 엑셀 출력
        </button>
      </div>

      {/* ⬇️ [신규] 안내문 추가 */}
      <div style={styles.infoBox}>
        <strong style={{ color: '#d9534f' }}>💡 예산 추정 방식 안내</strong>
        <ul style={styles.infoList}>
          <li><strong>총 예상 예산 (A)</strong>은 참여자의 마지막 근무월(종료월)을 기준으로 다음과 같이 추정됩니다.</li>
          <li><strong>참여종료일이 해당 월의 말일인 경우:</strong> 연차가 <strong>발생</strong>하는 것으로 가정합니다.</li>
          <li><strong>참여종료일이 말일이 아닌 경우 (중도 퇴사):</strong> 연차가 <strong>미발생</strong>하는 것으로 가정합니다.</li>
          <li>따라서, 만근이 예상되는 참여자는 [참여자 관리]에서 <strong>참여종료일을 해당 월의 말일로 설정</strong>해야 예산이 정확하게 계산됩니다.</li>
        </ul>
      </div>

      {/* 2. 데이터 카드 (요약) */}
      {loading && <p>예산 계산 중...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      
      {summary && !loading && (
        <>
        <h2 style={styles.sectionTitle}>전체 예산 요약</h2>
        <div style={styles.grid}>
          <div style={{...styles.card, ...styles.totalCard}}>
            <div style={styles.cardTitle}>사업종료일까지 연차수당예산 (A)</div>
            <div style={styles.cardValue}>
              {formatCurrency(summary.totalProjectedBudget)}
            </div>
          </div>
          
          <div style={{...styles.card, ...styles.usedCard}}>
            <div style={styles.cardTitle}>사용된 연차수당환산액 (B)</div>
            <div style={styles.cardValue}>
              {formatCurrency(summary.totalUsedLeaveValue)}
            </div>
          </div>

          <div style={{...styles.card, ...styles.paidCard}}>
            <div style={styles.cardTitle}>집행된 연차수당 (C)</div>
            <div style={styles.cardValue}>
              {formatCurrency(summary.totalPaidOutAmount)}
            </div>
          </div>
          
          <div style={{...styles.card, ...styles.remainingCard}}>
            <div style={styles.cardTitle}>향후 예상연차예산 (A - B - C)</div>
            <div style={styles.cardValue}>
              {formatCurrency(summary.remainingBudget)}
            </div>
          </div>
        </div>

        {/* 월별 연차수당 예산액 */}
        <div style={{ marginTop: '30px' }}>
          <h2 style={styles.sectionTitle}>월별 연차수당 예산액</h2>
          <div style={styles.monthlyGrid}>
            {([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]).map((m) => (
              <div key={m} style={styles.monthlyCard}>
                <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '6px' }}>{m}월</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#333' }}>
                  {formatCurrency((summary.monthlyTotalBudget || Array(12).fill(0))[m - 1])}
                </div>
              </div>
            ))}
            </div>
          </div>

        </>
      )}

      {/* 세부 사업별 내역 */}
      {details.length > 0 && !loading && (
        <div style={styles.tableContainer}>
          <h2 style={styles.sectionTitle}>세부 사업별 내역</h2>
          <div style={{
            marginBottom: '12px',
            padding: '12px 14px',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            fontSize: '0.9rem',
            color: '#475569',
            lineHeight: 1.6
          }}>
            기본으로 보여주는 예산 칼럼(월별·분기별 연차수당 예산액)에는 <strong>현재인원 기준으로 집계된 금액</strong>입니다.
            <br />
            <strong>추가인원 월(분기)별 예산액:</strong> 인건비 예산 관리의 &quot;추가고용여력&quot; 값을 사용하여, 해당 추가 인원이 기준월 이후 남은 개월 동안 근무한다고 가정한 연차수당 예산을 기존 월별·분기별 예산액에 더한 값입니다. 인건비 잔여 예산으로 추가 고용 가능한 인원의 연차 예산을 함께 확인할 때 사용합니다.
          </div>
          <div style={{ marginBottom: '12px', display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setDetailViewMode('year')}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: detailViewMode === 'year' ? '1px solid #2563eb' : '1px solid #e5e7eb',
                background: detailViewMode === 'year' ? '#2563eb' : '#ffffff',
                color: detailViewMode === 'year' ? '#ffffff' : '#374151',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              전체월(연간 합계)
            </button>
            <button
              type="button"
              onClick={() => setDetailViewMode('monthly')}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: detailViewMode === 'monthly' ? '1px solid #2563eb' : '1px solid #e5e7eb',
                background: detailViewMode === 'monthly' ? '#2563eb' : '#ffffff',
                color: detailViewMode === 'monthly' ? '#ffffff' : '#374151',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              월별 연차수당 예산액
            </button>
            <button
              type="button"
              onClick={() => setDetailViewMode('quarterly')}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: detailViewMode === 'quarterly' ? '1px solid #2563eb' : '1px solid #e5e7eb',
                background: detailViewMode === 'quarterly' ? '#2563eb' : '#ffffff',
                color: detailViewMode === 'quarterly' ? '#ffffff' : '#374151',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              분기별 연차수당 예산액
            </button>
            <button
              type="button"
              onClick={() => setDetailViewMode('monthlyWithAdditional')}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: detailViewMode === 'monthlyWithAdditional' ? '1px solid #2563eb' : '1px solid #e5e7eb',
                background: detailViewMode === 'monthlyWithAdditional' ? '#2563eb' : '#ffffff',
                color: detailViewMode === 'monthlyWithAdditional' ? '#ffffff' : '#374151',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              추가인원 월별 예산액
            </button>
            <button
              type="button"
              onClick={() => setDetailViewMode('quarterlyWithAdditional')}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: detailViewMode === 'quarterlyWithAdditional' ? '1px solid #2563eb' : '1px solid #e5e7eb',
                background: detailViewMode === 'quarterlyWithAdditional' ? '#2563eb' : '#ffffff',
                color: detailViewMode === 'quarterlyWithAdditional' ? '#ffffff' : '#374151',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              추가인원 분기별 예산액
            </button>
          </div>

          {detailViewMode === 'year' && (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{...styles.th, ...styles.tdName}}>사업명</th>
                  <th style={styles.th}>사업종료일까지 연차수당예산 (A)</th>
                  <th style={styles.th}>사용된 연차수당환산액 (B)</th>
                  <th style={styles.th}>집행된 연차수당 (C)</th>
                  <th style={styles.th}>향후 예상연차예산 (A - B - C)</th>
                </tr>
              </thead>
              <tbody>
                {details.map((project) => (
                  <tr key={project.projectId}>
                    <td style={{...styles.td, ...styles.tdName}}>{project.projectName}</td>
                    <td style={styles.td}>{formatCurrency(project.totalProjectedBudget)}</td>
                    <td style={styles.td}>{formatCurrency(project.totalUsedLeaveValue)}</td>
                    <td style={styles.td}>{formatCurrency(project.totalPaidOutAmount)}</td>
                    <td style={styles.td}>{formatCurrency(project.remainingBudget)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {detailViewMode === 'monthly' && (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{...styles.th, ...styles.tdName}}>사업명</th>
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                    <th key={m} style={styles.th}>{m}월</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {details.map((project) => {
                  const monthly = project.monthlyBudget || [];
                  return (
                    <tr key={project.projectId}>
                      <td style={{...styles.td, ...styles.tdName}}>{project.projectName}</td>
                      {[0,1,2,3,4,5,6,7,8,9,10,11].map((idx) => (
                        <td key={idx} style={styles.td}>
                          {formatCurrency(monthly[idx] || 0)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {detailViewMode === 'quarterly' && (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{...styles.th, ...styles.tdName}}>사업명</th>
                  <th style={styles.th}>1분기</th>
                  <th style={styles.th}>2분기</th>
                  <th style={styles.th}>3분기</th>
                  <th style={styles.th}>4분기</th>
                </tr>
              </thead>
              <tbody>
                {details.map((project) => {
                  const monthly = project.monthlyBudget || [];
                  const q1 = (monthly[0] || 0) + (monthly[1] || 0) + (monthly[2] || 0);
                  const q2 = (monthly[3] || 0) + (monthly[4] || 0) + (monthly[5] || 0);
                  const q3 = (monthly[6] || 0) + (monthly[7] || 0) + (monthly[8] || 0);
                  const q4 = (monthly[9] || 0) + (monthly[10] || 0) + (monthly[11] || 0);
                  return (
                    <tr key={project.projectId}>
                      <td style={{...styles.td, ...styles.tdName}}>{project.projectName}</td>
                      <td style={styles.td}>{formatCurrency(q1)}</td>
                      <td style={styles.td}>{formatCurrency(q2)}</td>
                      <td style={styles.td}>{formatCurrency(q3)}</td>
                      <td style={styles.td}>{formatCurrency(q4)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {detailViewMode === 'monthlyWithAdditional' && (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{...styles.th, ...styles.tdName}}>사업명</th>
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                    <th key={m} style={styles.th}>{m}월</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {details.map((project) => {
                  const monthly = project.monthlyBudgetWithAdditional || project.monthlyBudget || [];
                  return (
                    <tr key={project.projectId}>
                      <td style={{...styles.td, ...styles.tdName}}>{project.projectName}</td>
                      {[0,1,2,3,4,5,6,7,8,9,10,11].map((idx) => (
                        <td key={idx} style={styles.td}>
                          {formatCurrency(monthly[idx] || 0)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {detailViewMode === 'quarterlyWithAdditional' && (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{...styles.th, ...styles.tdName}}>사업명</th>
                  <th style={styles.th}>1분기</th>
                  <th style={styles.th}>2분기</th>
                  <th style={styles.th}>3분기</th>
                  <th style={styles.th}>4분기</th>
                </tr>
              </thead>
              <tbody>
                {details.map((project) => {
                  const monthly = project.monthlyBudgetWithAdditional || project.monthlyBudget || [];
                  const q1 = (monthly[0] || 0) + (monthly[1] || 0) + (monthly[2] || 0);
                  const q2 = (monthly[3] || 0) + (monthly[4] || 0) + (monthly[5] || 0);
                  const q3 = (monthly[6] || 0) + (monthly[7] || 0) + (monthly[8] || 0);
                  const q4 = (monthly[9] || 0) + (monthly[10] || 0) + (monthly[11] || 0);
                  return (
                    <tr key={project.projectId}>
                      <td style={{...styles.td, ...styles.tdName}}>{project.projectName}</td>
                      <td style={styles.td}>{formatCurrency(q1)}</td>
                      <td style={styles.td}>{formatCurrency(q2)}</td>
                      <td style={styles.td}>{formatCurrency(q3)}</td>
                      <td style={styles.td}>{formatCurrency(q4)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
      {summary && details.length === 0 && !loading && (
        <div style={{ ...styles.tableContainer, marginTop: '30px' }}>
          <h2 style={styles.sectionTitle}>세부 사업별 내역</h2>
          <p style={{ marginTop: '10px' }}>세부 사업 내역이 없습니다.</p>
        </div>
      )}

    </div>
  );
};

export default BudgetPage;